import type React from 'react';
import { useState } from 'react';

import { cx } from '@mage-ui/styled-system/css';
import {
  clearSection,
  comboboxEmpty,
  comboboxOption,
  dropdown,
  icon,
  inputSection,
  selectSection,
} from '@mage-ui/styled-system/recipes';
import { InputText, type InputTextProps } from '@/components/controls';
import { Icon } from '@/components/data-display';

import { DropdownBase, type DropdownBaseProps } from './DropdownBase';

export type ComboboxProps = Omit<
  DropdownBaseProps,
  'children' | 'onClick' | 'setQuery' | 'target'
> &
  Omit<InputTextProps, 'isClearable' | 'clearableIcon'> & {
    setSelectedOption: (
      option: { id: string | number; value: string } | undefined,
    ) => void;
    options: { id: string | number; value: string }[];
    creatable?: boolean;
    classNames?: {
      option?: string;
      empty?: string;
      create?: string;
    };
    create?: string;
    empty?: string | null;
    endSlot?: React.ReactNode;
    isClearable?: boolean;
    clearableIcon?: string;
  };

export const Combobox = ({
  defaultOpened = false,
  creatable,
  setSelectedOption,
  selectedOption,
  endSlot,
  options,
  classNames,
  create,
  empty,
  isClearable,
  clearableIcon,
  ...props
}: ComboboxProps) => {
  const [query, setQuery] = useState(selectedOption?.value ?? '');

  const shouldFilterOptions = options.every((item) => item.value !== query);
  const filteredOptions = shouldFilterOptions
    ? options.filter((item) =>
        item.value.toLowerCase().includes(query.toLowerCase().trim()),
      )
    : options;

  const dropdownOptions = filteredOptions.map((option) => (
    <DropdownBase.Option
      value={option.value}
      key={option.id}
      active={selectedOption?.id === option.id}
      classNames={{ option: classNames?.option ?? comboboxOption() }}
    >
      {option.value}
    </DropdownBase.Option>
  ));

  const MissingOption = () => {
    if (creatable && query) {
      return (
        <DropdownBase.Option
          value={query}
          key={query}
          classNames={{
            option: classNames?.create ?? comboboxOption({ type: 'create' }),
          }}
        >
          <Icon name='plus' classNames={icon({ size: 'm' })} />
          {`${create ?? 'Create'} '${query}'`}
        </DropdownBase.Option>
      );
    }
    return (
      empty !== null && (
        <DropdownBase.Empty
          classNames={{ empty: classNames?.empty ?? comboboxEmpty() }}
        >
          {empty ?? 'Nothing found'}
        </DropdownBase.Empty>
      )
    );
  };

  const onChange = (value: string | undefined) => {
    let selected = options.find((item) => item.value === value);
    if (!selected && creatable && value) {
      selected = { id: 'new', value };
    }
    setSelectedOption(selected);
  };
  const showDropdown =
    dropdownOptions.length > 0 || empty !== null || (creatable && query);
  return (
    <DropdownBase
      classNames={{ dropdown: showDropdown ? dropdown() : '' }}
      readOnly={false}
      target={
        <InputText
          value={query}
          classNames={{
            section:
              classNames?.section ??
              cx(
                inputSection(),
                isClearable && query ? clearSection() : selectSection(),
              ),
          }}
          {...props}
        />
      }
      selectedOption={selectedOption}
      setQuery={setQuery}
      setSelectedOption={(option) => onChange(option)}
      endSlot={endSlot}
      width='target'
      isClearable={isClearable}
      clearableIcon={clearableIcon}
    >
      {showDropdown &&
        (dropdownOptions.length > 0 ? dropdownOptions : <MissingOption />)}
    </DropdownBase>
  );
};
